"use client"

import type React from "react"
import { useState } from "react"
import { apiClient } from "@/lib/api-client"
import { LEGAL_DOCUMENT_VERSIONS, CONSENT_TYPE } from "@/lib/legal-versions"

const LABEL =
  "font-mono text-[11px] uppercase tracking-[0.12em] text-[rgba(11,16,32,0.55)]"

const INPUT_CLASS =
  "w-full h-11 rounded-none border-0 border-b border-[rgba(11,16,32,0.20)] bg-transparent px-0 text-[15px] focus-visible:border-[#0b1020] focus-visible:outline-none"

const TEXTAREA_CLASS =
  "w-full min-h-[120px] rounded-none border border-[rgba(11,16,32,0.20)] bg-transparent p-3 text-[15px] focus-visible:border-[#0b1020] focus-visible:outline-none resize-y"

type Errors = Record<string, string[]>

export function ContactFormSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle")
  const [errors, setErrors] = useState<Errors>({})
  const [acceptPdn, setAcceptPdn] = useState(false)
  const [acceptPrivacy, setAcceptPrivacy] = useState(false)

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target
    setForm((p) => ({ ...p, [name]: value }))
    const cap = name.charAt(0).toUpperCase() + name.slice(1)
    if (errors[cap]) {
      setErrors((prev) => {
        const u = { ...prev }
        delete u[cap]
        return u
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setErrors({})
    try {
      const response = await apiClient.submitFeedback({
        ...form,
        consents: [
          { type: CONSENT_TYPE.pdnProcessing, version: LEGAL_DOCUMENT_VERSIONS.privacyPolicy },
          { type: CONSENT_TYPE.privacyPolicy, version: LEGAL_DOCUMENT_VERSIONS.privacyPolicy },
        ],
      })
      if (response.success) {
        setStatus("success")
        setForm({ name: "", email: "", phone: "", company: "", message: "" })
        setTimeout(() => setStatus("idle"), 5000)
      } else {
        setStatus("error")
        setTimeout(() => setStatus("idle"), 5000)
      }
    } catch (err: unknown) {
      const e = err as { status?: number; problem?: { errors?: Errors } }
      if (e?.status === 400 && e?.problem?.errors) {
        setErrors(e.problem.errors)
        setTimeout(() => setErrors({}), 5000)
      } else {
        setStatus("error")
        setTimeout(() => setStatus("idle"), 5000)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const fieldError = (name: string) => {
    const cap = name.charAt(0).toUpperCase() + name.slice(1)
    return errors[cap]?.[0]
  }

  return (
    <section
      id="contact"
      className="border-b border-[rgba(11,16,32,0.10)] bg-white scroll-mt-[72px]"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-14 py-20 md:py-24">
        <div className={LABEL}>§ 08 · связаться</div>
        <h2
          className="mt-3 text-[32px] md:text-[44px] font-semibold leading-[1.05] tracking-[-0.025em] max-w-[640px]"
          style={{ fontFamily: "var(--font-manrope), Inter, sans-serif" }}
        >
          Остались вопросы?
        </h2>
        <p className="mt-3 max-w-[560px] text-[14px] leading-[1.5] text-[rgba(11,16,32,0.70)]">
          Напишите нам — ответим в течение 2 часов в рабочее время. Пн–Пт, 9:00–18:00&nbsp;МСК.
        </p>

        <div className="mt-12 grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14">
          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-6 border-t border-t-[#0b1020] pt-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Field label="Имя" name="name" required value={form.name} onChange={handleChange} error={fieldError("name")} />
              <Field label="Email" name="email" type="email" required value={form.email} onChange={handleChange} error={fieldError("email")} />
              <Field label="Телефон" name="phone" type="tel" value={form.phone} onChange={handleChange} error={fieldError("phone")} />
              <Field label="Компания" name="company" value={form.company} onChange={handleChange} error={fieldError("company")} />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="message" className={LABEL}>
                Сообщение <span className="text-[#ea580c]">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                required
                value={form.message}
                onChange={handleChange}
                placeholder="Расскажите о вашей задаче"
                className={TEXTAREA_CLASS}
              />
              {fieldError("message") && (
                <div className="text-[12px] text-[#991b1b]">{fieldError("message")}</div>
              )}
            </div>

            {status === "success" && (
              <div className="border border-[rgba(11,16,32,0.10)] bg-[#f5f6fb] p-3 text-[13px]">
                Спасибо! Мы получили ваше сообщение и ответим в ближайшее время.
              </div>
            )}
            {status === "error" && (
              <div className="border border-[rgba(220,38,38,0.30)] bg-[rgba(220,38,38,0.06)] p-3 text-[13px] text-[#991b1b]">
                Не удалось отправить. Попробуйте позже или напишите в Telegram.
              </div>
            )}

            <div className="space-y-2 pt-2">
              <label className="flex items-start gap-2 text-[13px] leading-[1.5] text-[rgba(11,16,32,0.70)] cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptPdn}
                  onChange={(e) => setAcceptPdn(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-[#0b1020]"
                  required
                />
                <span>
                  Я даю согласие на обработку моих персональных данных в соответствии с{" "}
                  <a href="/privacy" target="_blank" rel="noreferrer" className="font-medium text-[#0b1020] underline underline-offset-[3px]">
                    Политикой конфиденциальности
                  </a>
                  .
                </span>
              </label>
              <label className="flex items-start gap-2 text-[13px] leading-[1.5] text-[rgba(11,16,32,0.70)] cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptPrivacy}
                  onChange={(e) => setAcceptPrivacy(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-[#0b1020]"
                  required
                />
                <span>
                  Я ознакомлен(а) с{" "}
                  <a href="/privacy" target="_blank" rel="noreferrer" className="font-medium text-[#0b1020] underline underline-offset-[3px]">
                    Политикой конфиденциальности
                  </a>{" "}
                  и принимаю её условия.
                </span>
              </label>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
              <button
                type="submit"
                disabled={isSubmitting || !acceptPdn || !acceptPrivacy}
                className="inline-flex items-center justify-center bg-[#0b1020] text-[#f4f4f5] text-[14px] font-semibold tracking-[0.015em] px-6 py-3.5 rounded-[4px] hover:opacity-92 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Отправляем…" : "Отправить сообщение"}
              </button>
            </div>
          </form>

          {/* Direct contacts */}
          <div className="grid grid-cols-1 border-t border-t-[#0b1020]">
            <a
              href="mailto:support@маркировочная.рф"
              className="block py-6 border-b border-[rgba(11,16,32,0.10)] no-underline text-[#0b1020] hover:bg-[#f5f6fb] -mx-4 px-4 transition-colors"
            >
              <span className={`${LABEL} text-[#ea580c]`}>email · ответ в 2 ч</span>
              <div
                className="mt-3 text-[22px] md:text-[24px] font-semibold leading-[1.1] tracking-[-0.015em]"
                style={{ fontFamily: "var(--font-manrope), Inter, sans-serif" }}
              >
                support@маркировочная.рф
              </div>
              <div className="mt-2 text-[13px] text-[rgba(11,16,32,0.70)]">
                Напишите нам письмо →
              </div>
            </a>
            <a
              href="https://t.me/metka_support_bot"
              target="_blank"
              rel="noopener noreferrer"
              className="block py-6 no-underline text-[#0b1020] hover:bg-[#f5f6fb] -mx-4 px-4 transition-colors"
            >
              <span className={`${LABEL} text-[#ea580c]`}>telegram · ответ в 2 ч</span>
              <div
                className="mt-3 text-[22px] md:text-[24px] font-semibold leading-[1.1] tracking-[-0.015em]"
                style={{ fontFamily: "var(--font-manrope), Inter, sans-serif" }}
              >
                @metka_support_bot
              </div>
              <div className="mt-2 text-[13px] text-[rgba(11,16,32,0.70)]">
                Открыть чат-бот →
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

function Field({
  label,
  name,
  type = "text",
  required,
  value,
  onChange,
  error,
}: {
  label: string
  name: string
  type?: string
  required?: boolean
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  error?: string
}) {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={name} className={LABEL}>
        {label} {required && <span className="text-[#ea580c]">*</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        className={INPUT_CLASS}
      />
      {error && <div className="text-[12px] text-[#991b1b]">{error}</div>}
    </div>
  )
}
